/* eslint-disable react/no-unused-prop-types */
// this is used because all the links are actually used
// inside a function and to keep the naming somewhere it's stll saved here
import React, { useEffect, useState } from 'react';

import { MapTo, Utils } from '@adobe/aem-react-editable-components';
import { useFormik } from 'formik';
import { useCurrentContracts } from 'hooks/UseCurrentContracts';
import { useCurrentExperts } from 'hooks/UseCurrentExperts';
import { useCurrentInstaller } from 'hooks/UseCurrentInstaller';
import { useCurrentSeals } from 'hooks/UseCurrentSeals';
import { useFormUtilityData } from 'hooks/useFormUtilityData';
import { isUndefined } from 'lodash';
import PropTypes from 'prop-types';

import { DebFormTestingBanner } from 'components/FormHelper/FormTestingBanners/DebFormTestingBanner';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { RenderAemComponent } from 'components/RenderAemComponent/RenderAemComponent';

import { DEB_ACTIVE_PAGE } from 'global/constants';

import {
  parseAEMDatapolicyProps,
  parseAEMInstallerContractProps,
  parseAEMSelfDeclarationCompanyFacilityProps,
  parseTrainingCertificateExtensionLink,
} from 'utils/AEMPropUtils';
import { getInstallerStatusSummary, isFachinteressent } from 'utils/InstallerUtils';
import { QualificationBasisDataProvider } from 'utils/qualificationBasisData';

import { DebFIFirstPage } from './DebFIFirstPage';
import { DebFISecondPage } from './DebFISecondPage';
import { DebFormFirstPage } from './DebFormFirstPage';
import { DebFormSecondPage } from './DebFormSecondPage';
import { prepareDEBFICompleteData } from './Utils/DebFIFormDataUtil';
import { prepareDEBCompleteData } from './Utils/DebFormDataUtil';
import { YDebFIFormSchema } from './ValidationStructure/YDebFIFormSchema';
import { YDebFormSchema } from './ValidationStructure/YDebFormSchema';

import './DebForm.sass';

export const DebForm = (props) => {
  const [page, setPage] = useState(DEB_ACTIVE_PAGE.FORM_PAGE);
  const [initialValues, setInitialValues] = useState();
  const [installerStatus, setInstallerStatus] = useState();
  const { installer, isLoading: isInstallerLoading } = useCurrentInstaller();
  const { experts, isLoading: isExpertsLoading } = useCurrentExperts();
  const { contracts, isLoading: isContractsLoading } = useCurrentContracts();
  const { seals, isLoading: isSealsLoading } = useCurrentSeals();
  const { comboBoxOptions, isLoading: isLoadingFormUtilityData } = useFormUtilityData();
  const [isLoading, setIsLoading] = useState(true);
  const isDebFI = isFachinteressent(installer);

  useEffect(() => {
    if (installer) {
      setInstallerStatus(getInstallerStatusSummary(installer));
    }
  }, [installer]);

  useEffect(() => {
    if (
      !isInstallerLoading &&
      installer &&
      installerStatus &&
      !isExpertsLoading &&
      !isUndefined(experts) &&
      !isLoadingFormUtilityData &&
      (isDebFI ||
        (!isContractsLoading && !isUndefined(contracts) && !isSealsLoading && !isUndefined(seals)))
    ) {
      setInitialValues(
        isDebFI
          ? prepareDEBFICompleteData(installer, experts, installerStatus)
          : prepareDEBCompleteData(installer, experts, contracts, seals, installerStatus)
      );
      setIsLoading(false);
    }
  }, [
    isInstallerLoading,
    isExpertsLoading,
    isContractsLoading,
    isSealsLoading,
    installer,
    experts,
    contracts,
    seals,
    installerStatus,
    isLoadingFormUtilityData,
    isDebFI,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const formik = useFormik({
    initialValues,
    validationSchema: isDebFI
      ? YDebFIFormSchema()
      : YDebFormSchema(installerStatus, initialValues?.doesNeedAvaGuestPostal),
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
  });
  const isEditor = Utils.isInEditor();

  if (isEditor) {
    return <RenderAemComponent componentProps={props} name="DebForm" />;
  }

  if (isLoading || isLoadingFormUtilityData) {
    return <LoadingIndicator />;
  }
  const {
    energyAgreementLink,
    gasAgreementLink,
    expertQualificationCertificateGasLink,
    expertQualificationCertificateEnergyLink,
  } = props;

  if (!formik.values) {
    return <LoadingIndicator />;
  }
  const form = (
    <div className="deb-form">
      <div className="deb-form__container">
        <DebFormTestingBanner activePage={page} installerStatusSummary={installerStatus} />
        <div
          className="deb-form__container-content"
          style={{ display: page !== DEB_ACTIVE_PAGE.FORM_PAGE ? 'none' : 'block' }}>
          {isDebFI ? (
            <DebFIFirstPage
              installer={installer}
              formik={formik}
              initialValues={initialValues}
              setPage={setPage}
              comboBoxOptions={comboBoxOptions}
            />
          ) : (
            <DebFormFirstPage
              installer={installer}
              formik={formik}
              initialValues={initialValues}
              energyAgreementLink={energyAgreementLink}
              gasAgreementLink={gasAgreementLink}
              setPage={setPage}
              comboBoxOptions={comboBoxOptions}
            />
          )}
        </div>
        {page === DEB_ACTIVE_PAGE.FORM_UPLOAD &&
          (isDebFI ? (
            <DebFISecondPage
              initialValuesFirstPage={initialValues}
              valuesFirstPage={formik.values}
              installer={installer}
              setPage={setPage}
              dataPolicyDocuments={parseAEMDatapolicyProps(props)}
            />
          ) : (
            <DebFormSecondPage
              initialValuesFirstPage={initialValues}
              valuesFirstPage={formik.values}
              installer={installer}
              setPage={setPage}
              expertQualificationCertificateGasLink={expertQualificationCertificateGasLink}
              expertQualificationCertificateEnergyLink={expertQualificationCertificateEnergyLink}
              installerContractLink={parseAEMInstallerContractProps(props, installer)}
              trainingCertificateExtensionLink={parseTrainingCertificateExtensionLink(props)}
              selfDeclarationCompanyFacilityLink={parseAEMSelfDeclarationCompanyFacilityProps(
                props
              )}
              dataPolicyDocuments={parseAEMDatapolicyProps(props)}
            />
          ))}
      </div>
    </div>
  );
  return <QualificationBasisDataProvider enabled={!isDebFI}>{form}</QualificationBasisDataProvider>;
};

// Look on top for eslint rule explanation
DebForm.propTypes = {
  expertQualificationCertificateGasLink: PropTypes.string,
  expertQualificationCertificateEnergyLink: PropTypes.string,
  installerContractLink: PropTypes.string,
  installerContractLinkEENG: PropTypes.string,
  installerContractLinkNNNG: PropTypes.string,
  installerContractLinkHANSG: PropTypes.string,
  selfDeclarationCompanyFacilityLink: PropTypes.string,
  selfDeclarationCompanyFacilityNNNGLink: PropTypes.string,
  selfDeclarationCompanyFacilityLinkMTNEnergy: PropTypes.string,
  selfDeclarationCompanyFacilityLinkMTNGas: PropTypes.string,
  trainingCertificateExtensionLink: PropTypes.string,
  gasAgreementLink: PropTypes.string,
  energyAgreementLink: PropTypes.string,
  datapolicyEDILink: PropTypes.string,
  datapolicyAVALink: PropTypes.string,
  datapolicySHNGLink: PropTypes.string,
  datapolicyBAGLink: PropTypes.string,
  datapolicyEENGLink: PropTypes.string,
  datapolicyHANSGLink: PropTypes.string,
  datapolicyNNNGLink: PropTypes.string,
  datapolicyEDILabel: PropTypes.string,
  datapolicyAVALabel: PropTypes.string,
  datapolicySHNGLabel: PropTypes.string,
  datapolicyBAGLabel: PropTypes.string,
  datapolicyEENGLabel: PropTypes.string,
  datapolicyHANSGLabel: PropTypes.string,
  datapolicyNNNGLabel: PropTypes.string,
};

DebForm.defaultProps = {
  expertQualificationCertificateGasLink: '',
  expertQualificationCertificateEnergyLink: '',
  installerContractLink: '',
  installerContractLinkEENG: '',
  installerContractLinkNNNG: '',
  installerContractLinkHANSG: '',
  selfDeclarationCompanyFacilityLink: '',
  selfDeclarationCompanyFacilityNNNGLink: '',
  selfDeclarationCompanyFacilityLinkMTNEnergy: '',
  selfDeclarationCompanyFacilityLinkMTNGas: '',
  trainingCertificateExtensionLink: '',
  gasAgreementLink: '',
  energyAgreementLink: '',
  datapolicyEDILink: '',
  datapolicyAVALink: '',
  datapolicySHNGLink: '',
  datapolicyBAGLink: '',
  datapolicyEENGLink: '',
  datapolicyHANSGLink: '',
  datapolicyNNNGLink: '',
  datapolicyEDILabel: '',
  datapolicyAVALabel: '',
  datapolicySHNGLabel: '',
  datapolicyBAGLabel: '',
  datapolicyEENGLabel: '',
  datapolicyHANSGLabel: '',
  datapolicyNNNGLabel: '',
};

DebForm.defaultProps = {};

MapTo(['e-fix/components/content/datenerfassungsblatt'])(DebForm);
